"use client";

import { Building2, User } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OrgType } from "@/lib/auth/resolve-org-type";
import { DEMO_ORG_MODE_SWITCH_COPY as COPY } from "./demo-org-mode-switch.copy";

interface OrgModeOption {
  id: OrgType;
  label: string;
  /** Sem o "Sou": na linha compacta o verbo repetido nos dois lados é ruído. */
  shortLabel: string;
  hint: string;
  icon: typeof User;
}

/**
 * Mesmos rótulos e ícones do seletor de público da seção de preços — é a mesma
 * pergunta feita duas vezes na mesma página, e responder diferente confundiria.
 */
const OPTIONS: OrgModeOption[] = [
  {
    id: "creator",
    get label() {
      return COPY.creator.label;
    },
    get shortLabel() {
      return COPY.creator.shortLabel;
    },
    get hint() {
      return COPY.creator.hint;
    },
    icon: User,
  },
  {
    id: "collab",
    get label() {
      return COPY.collab.label;
    },
    get shortLabel() {
      return COPY.collab.shortLabel;
    },
    get hint() {
      return COPY.collab.hint;
    },
    icon: Building2,
  },
];

interface DemoOrgModeSwitchProps {
  value: OrgType;
  onChange: (mode: OrgType) => void;
  /**
   * `"marketing"` é a pílula da home, com ícone e a dica embaixo — lá ela é a
   * única coisa que explica o que muda na tela. `"compact"` é a da página
   * `/demo`, onde o switch divide uma linha de 52px com o voltar e a chamada de
   * cadastro: ali ele é controle secundário, então perde o verde (que fica só
   * no botão de cadastro), o ícone, o negrito e a dica — que passa a viver no
   * rótulo acessível. Direção aprovada pelo dono em 2026-09-17.
   */
  size?: "marketing" | "compact";
  className?: string;
}

/**
 * Troca o tipo de conta encenado no demo. Fica acima do frame — e não junto das
 * `FeatureTabs` — porque não é mais uma tela: é o mesmo produto visto do outro
 * lado, e as nove abas mudam junto.
 */
export function DemoOrgModeSwitch({
  value,
  onChange,
  size = "marketing",
  className,
}: DemoOrgModeSwitchProps) {
  const active = OPTIONS.find((option) => option.id === value) ?? OPTIONS[0];
  const isCompact = size === "compact";

  return (
    <div
      className={cn(
        "flex flex-col items-center",
        isCompact ? "gap-0" : "gap-2",
        className,
      )}
    >
      <div
        role="tablist"
        aria-label={COPY.ariaLabel}
        className={cn(
          "inline-flex",
          isCompact
            ? "gap-0.5 rounded-full bg-tami-light/[0.07] p-[3px]"
            : "gap-1 rounded-full border border-tami-light/20 bg-tami-light/5 p-1.5",
        )}
      >
        {OPTIONS.map((option) => {
          const isSelected = option.id === value;
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => onChange(option.id)}
              data-testid={`demo-org-mode-${option.id}`}
              aria-label={
                isCompact ? `${option.label} — ${option.hint.toLowerCase()}` : undefined
              }
              title={isCompact ? option.hint : undefined}
              className={cn(
                "flex items-center rounded-full transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-tami-green focus-visible:ring-offset-2 focus-visible:ring-offset-tami-dark",
                isCompact
                  ? "gap-2 px-4 py-1.5 text-[13px]"
                  : "gap-2 px-4 py-2 text-xs font-bold sm:px-6 sm:py-2.5 sm:text-sm",
                isSelected && isCompact && "bg-tami-light/[0.14] font-semibold text-tami-light",
                isSelected && !isCompact && "bg-tami-green text-tami-dark",
                !isSelected && isCompact && "font-medium text-tami-light/60 hover:text-tami-light",
                !isSelected && !isCompact && "text-tami-light hover:bg-tami-light/10",
              )}
            >
              {isCompact ? (
                /*
                 * O ponto verde no lugar do fundo verde: marca o lado ativo sem
                 * competir com a chamada de cadastro, que é a única coisa verde
                 * da linha.
                 */
                isSelected ? (
                  <span aria-hidden className="size-1.5 rounded-full bg-tami-green" />
                ) : null
              ) : (
                <Icon className="size-4" />
              )}
              {isCompact ? option.shortLabel : option.label}
            </button>
          );
        })}
      </div>

      {/*
        Na home a dica é necessária: sem ela, "Sou Collab" não diz o que muda na
        tela abaixo. Na página ela sairia da linha única e viraria uma segunda
        linha de base — lá o sentido vive no rótulo acessível de cada lado.
      */}
      {!isCompact && (
        <p
          aria-live="polite"
          className="h-4 text-center text-[11px] leading-4 text-tami-light/60 sm:text-xs"
        >
          {active.hint}
        </p>
      )}
    </div>
  );
}
